<template>
  <!-- Farbiger Grund. Jedes Projekt und jeder Bereich malt ihn ueber SEINE
       eigenen Zeilen, und nur dort, wo es ihn ueberhaupt gibt. Ein
       Leistungsbereich hat in der Pause des Hauptprojekts keine Flaeche - dort
       bleibt nur die Schraffur -, eine Unterkunft laeuft durch und behaelt
       ihren Grund. Vorher malte das Hauptprojekt eine Flaeche ueber alles, auch
       ueber Zeiten, in denen ein Bereich gar nicht existiert.
       Ausserhalb des isopen-Blocks, damit ein zugeklappter Bereich seine
       Kopfzeile behaelt statt als helle Luecke im Projekt zu stehen. Nur ein
       zugeklapptes HAUPTPROJEKT bleibt aussen vor: dessen Balken ist selbst
       gefuellt (projectColor in Task.vue), ein Grund darunter machte ihn
       dunkler - dieselbe Doppelung wie seinerzeit bei der Pausenschraffur. -->
  <template v-if="!props.isprintView && !view.isZoom() && (props.isopen || props.project.parent_id)">
    <div v-for="(pd, i) in displayPeriods" :key="`bg-${i}`"
         :style="periodBackgroundStyle(pd)" class="projectbackground"></div>
  </template>

  <template v-if="!props.isprintView && props.isopen && !view.isZoom()">
    <!-- Pausen zwischen zwei Abschnitten (TIM-60): schraffiert statt leer, damit
         eine Pause nicht wie das Ende des Projekts aussieht. -->
    <div v-for="(gp, i) in displayGaps" :key="`pausebg-${i}`"
         :style="pauseBackgroundStyle(gp)" class="projectpause"></div>
    <!-- Outline um das gesamte Projekt (TIM-61): ein durchgehendes Rechteck von
         Projektanfang bis Projektende, Pausen liegen innen. Ersetzt die Farbe
         des Kopfbalkens als Kennzeichen der Projektzugehoerigkeit. -->
    <!-- Nur das Hauptprojekt zieht den Rahmen: er umfasst ohnehin die
         Unterprojekte, ein eigener je Unterprojekt wuerde das Ganze in Kaesten
         unterteilen statt es zu klammern. -->
    <div v-if="!props.project.parent_id" :style="projectOutlineStyle()" class="projectoutline"></div>
  </template>
  
  <!-- Rahmen um einen Bereich (Entwurf Bjoern, entschieden 09.09.). Zugeklappt
       umfasst er die Titelzeile, aufgeklappt den ganzen Bereich. Innen liegt der
       Grund des Hauptprojekts, aussen bleibt es weiss - das ergibt sich von
       selbst, weil jeder Bereich seinen Grund nur ueber seinen eigenen Zeitraum
       malt. -->
  <div v-if="!props.isprintView && props.project.parent_id && !view.isZoom()"
       :style="subprojectOutlineStyle()" class="projectoutline sub"></div>

  <div
    v-if="!props.isprintView"
    :id="`${props.project.id}`"
    :style="[{ height: `${getRowHeight(props.isprintView)}px`}, projectRowOpenStyle()]"
    class="project-row resource-row !border-b !border-gray-100 !border-[1px]"
	  :class="{ spacer: props.project.isspacer }"
    ref="projectEl"
	  @handleClick="deselect"
	  @pointerdown="deselect"
  >  
      <!-- Die Pausen im Kopfbalken (TIM-60). Stehen vor den Balken, damit die
           Balken im Zweifel obenauf liegen. -->
      <template v-if="!props.isprintView">
        <div v-for="(gp, i) in displayGaps" :key="`pausebar-${i}`"
             :style="pauseBarStyle(gp)" class="projectpause"></div>
      </template>

      <!-- Ein Balken je Abschnitt. Der erste behaelt die bisherige ID, weil
           anderer Code das Element ueber die Projekt-ID sucht. -->
      <Task v-for="(pd, i) in displayPeriods"
          :key="`${props.project.id}-${i}-${updateProject}`"
	        :isopen="props.isopen"
          :visibleUserIds="props.resourceFilter?.visible"
	        :id="i === 0 ? `${props.project.id}` : `${props.project.id}-p${i}`"
			    :isproject="true"
	        :project="props.project"
          :isprintView="props.isprintView"
			    :bookings="props.bookings"
	        :label="i === 0 ? props.project.name : ''"
	        :start="pd.start"
	        :end="pd.end"
          :locked="displayPeriods.length > 1"
          :startDate="props.startDate"
          :endDate="props.endDate"
			    :inproject="true"
			    :configs="props.configs"
			    @bookingDone="handleBookingDone"
			    @cancel="handleCancel"
			    @taskChange="projectChangeHanlder"
	  />
  </div>

  <div v-if="props.isopen" v-for="(u, idx) in activeUsers"
      :id="`${props.project.id}-${u.id}`"
      :style="[{ height: `${getRowHeight(props.isprintView)}px`}, userRowOpenStyle(idx)]"
      :class="['resource-row !border-b !border-[1px]', props.isprintView ? '!border-gray-200' : '!border-gray-100']"  ref="projectEl">
       <Task v-for="b in getUserBookings(pid2pnames, props.bookings, props.project, u, props.isprintView, props.projects)"
	    :id="`${props.project.id + '-' + u.id + '-' + b.id}`"
		  :isproject="false"
      :isprintView="props.isprintView"
      :project="props.project"
		  :user="u"
		  :booking="b"
		  :bookings="props.bookings"
	    :label="`${b.label}`"
	    :start="b.start"
	    :end="b.end"
      :startDate="props.startDate"
      :endDate="props.endDate"
		  :inproject="b.inproject"
		  :derived="b.derived || false"
		  :derivedFrom="b.derivedFrom || ''"
		  :configs="props.configs"
		  @bookingDone="handleBookingDone"
	  />

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import {bus, cleanx, getUserBookings, getWidthPerDay, getRowHeight, dayMs, applyResoureFilter, effectivePeriods} from "./planner.js";
import {fromIso, toIso, addDays} from '@/utils/dateUtils';
import {transparent, readableProjectColor} from '@/utils/globals';
import Task from './Task.vue';
import * as d3 from 'd3';

import {useViewStore} from "@/stores/viewStore"

const view = useViewStore();

const props = defineProps({
  startDate: Date,
  endDate: Date,
  project: Object,
  isprintView: Boolean,
  projects: {type: Array, required: true},
  users: {type: Array, required: true},
  bookings: {type: Array, required: true},
  isopen: {type: Boolean, required: true},
  resourceFilter: {type: Object, required: true},
  // Zeilen der Unterprojekte, die der Rahmen mitumfassen soll (TIM-73).
  subRows: {type: Number, default: 0},
  configs: { type: Array, required: true },
})

const emit = defineEmits(['bookingDone', "projectChangeDone", "deselect", "cancel"])

const widthPerDay = getWidthPerDay(props.isprintView, view.zoomWidthPerDay)

const updateProject = ref(0)
// Local open/close state
const isOpen = ref(false)

const activeUsers = computed(() =>
  props.users.filter(u => applyResoureFilter(props.resourceFilter, props.project, u))
)

function projectRowOpenStyle() { return {} }
function userRowOpenStyle(_idx) { return {} }

// Ref to the main element
const projectEl = ref(null)

const startDateStr = props.configs.find(c => c.name === "startdate").value;
const endDateStr = props.configs.find(c => c.name === "enddate").value;
const startDate = fromIso(startDateStr);
const endDate   = fromIso(endDateStr);

const totalDays = (endDate - startDate) / dayMs;

const x = d3.scaleUtc().domain([startDate, endDate]).range([0, totalDays * widthPerDay]);

const borderWidth = 10;

const startRef = ref(props.project.start)
const endRef   = ref(props.project.end)

watch(
  () => [props.project.start, props.project.end],
  ([newStart, newEnd]) => {
    startRef.value = newStart
    endRef.value   = newEnd
  }
)

// Die Abschnitte, die gezeichnet werden (TIM-38). Bei genau einem Abschnitt
// kommen Anfang und Ende weiterhin aus startRef/endRef - die werden beim Ziehen
// laufend aktualisiert, der bisherige Ablauf bleibt damit unveraendert. Erst ab
// zwei Abschnitten wird aus dem Projekt gelesen und der Balken gesperrt.
const displayPeriods = computed(() => {
  if (props.project.isspacer) return []

  // Ein Leistungsbereich erbt die Abschnitte seines Hauptprojekts: er laeuft nur,
  // solange dieses laeuft, also sind dessen Pausen auch seine (TIM-73). Damit
  // bekommt er automatisch dieselbe Schraffur, dieselben Balkenlaengen und
  // dieselbe Behandlung von Fremdbuchungen.
  const geerbt = effectivePeriods(props.project, props.projects)
  if (props.project.parent_id && geerbt.length > 0) return geerbt

  const periods = props.project.periods ?? []
  if (periods.length > 1) return periods
  return [{ start: startRef.value, end: endRef.value }]
});

// Die Pausen zwischen den Abschnitten (TIM-60). Gerechnet wird gleich in Pixeln
// statt in Tagen: die Kanten muessen exakt an den Balken der Nachbarabschnitte
// anschliessen, und die sitzen in Task.vue um feste 5 px versetzt.
const displayGaps = computed(() => {
  const periods = displayPeriods.value
  const gaps = []
  for (let i = 1; i < periods.length; i++) {
    const left  = cleanx(x, fromIso(periods[i - 1].end)) + widthPerDay
    const right = cleanx(x, fromIso(periods[i].start))
    if (right > left) gaps.push({ left, right })
  }
  return gaps
});

// 45-Grad-Schraffur in der Projektfarbe, von links unten nach rechts oben.
// (-45deg dreht die Gradientenachse, die Striche stehen senkrecht dazu.)
// Die Luecke ist breiter als der Strich, damit eine Pause immer heller wirkt
// als ein gefuellter Balken.
function hatch(alpha) {
  const stripe = transparent(props.project.color, alpha)
  return `repeating-linear-gradient(-45deg, ${stripe} 0 3px, transparent 3px 10px)`
}

// Aufgeklapptes Projekt: die Schraffur laeuft ueber die Personenzeilen - aber
// erst UNTERHALB der Kopfzeile. Vorher begann sie bei 0 und lag damit ueber dem
// Pausenbalken der Kopfzeile, was dort eine doppelte Schraffur ergab.
//
// Und nur ueber die EIGENEN Personenzeilen, nicht ueber die der Bereiche: ein
// Leistungsbereich erbt die Abschnitte seines Hauptprojekts und zeichnet
// dieselbe Pause noch einmal selbst - uebereinander ergab das eine sichtbar
// dunklere Schraffur. Eine Unterkunft hat eigene Abschnitte, laeuft ueber die
// Pause hinweg und zeichnet dort deshalb gar nichts; die Schraffur des
// Hauptprojekts haette ihr eine Pause uebergestuelpt, die sie nicht hat.
function pauseBackgroundStyle(gap) {
  const rh = getRowHeight(props.isprintView);

  return {
	position: 'absolute',
	// marginTop statt top: ohne top-Angabe sitzt das Rechteck an seiner
	// statischen Position im Fluss, also am Anfang des Projektblocks. Ein top
	// wuerde es stattdessen am naechsten positionierten Vorfahren ausrichten -
	// und der liegt ganz oben im Timesheet, wodurch die Schraffur ueber die
	// Projekte darueber lief.
	marginTop: `${rh}px`,
	left: `${gap.left}px`,
	width: `${gap.right - gap.left}px`,
	height: `${rh * activeUsers.value.length}px`,
	background: hatch(0.35),
	zIndex: 0,
	pointerEvents: 'none',
  }
}

// Kopfzeile: dieselben Masse wie taskStyle in Task.vue, sonst entsteht an der
// Nahtstelle zum Balken eine Stufe.
//
// Beide Kanten liegen 5 px vor ihrem Datum, weil die Balken das auch tun
// (Task.vue: leftPx zieht 5 ab, widthPx rechnet sie wieder heraus). Der Balken
// davor endet damit bei gap.left - 5. Ohne den Versatz links klaffte dort eine
// 5-px-Luecke, die der border-radius des Balkens am Desktop fast verdeckte,
// auf dem Telefon aber deutlich zu sehen war.
function pauseBarStyle(gap) {
  return {
	position: 'absolute',
	top: '3px',
	left: `${gap.left - 5}px`,
	width: `${Math.max(gap.right - gap.left, 0)}px`,
	height: `${getRowHeight(props.isprintView) - 12}px`,
	// Entscheidend ist, ob der Balken ueberhaupt eine Flaeche hat - nicht der
	// Klappzustand allein.
	//
	// Gefuellt ist er nur beim zugeklappten Hauptprojekt: dort bekommt die Pause
	// denselben Grund wie seine leeren Stellen (Projektfarbe auf 0.5, wie
	// projectColor in Task.vue) und darauf die Schraffur in voller Farbe, damit
	// der Balken ein Element bleibt und die Pause sich trotzdem abhebt.
	//
	// Sonst traegt die Schraffur die ganze Flaeche allein und darf schwach sein.
	// Das gilt auch fuer einen ZUGEKLAPPTEN Bereich: der ist in beiden Zustaenden
	// farblos, bekam aber den gefuellten Grund des zugeklappten Hauptprojekts -
	// eine dunkelblaue Bahn mitten in einer sonst leeren Zeile.
	background: (!props.isopen && !props.project.parent_id)
	  ? `${hatch(1)}, ${transparent(props.project.color, 0.5)}`
	  : hatch(0.25),
	zIndex: 0,
	pointerEvents: 'none',
  }
}

// Ein Rahmen ueber die volle Projektspanne, in derselben Geometrie wie die
// Abschnitts-Hintergruende, damit die Kanten buendig sitzen. pointerEvents aus,
// sonst faengt das Rechteck die Klicks fuer Auswahl und Drag ab.
function projectOutlineStyle() {
  const periods = displayPeriods.value
  const left  = cleanx(x, fromIso(periods[0].start))
  const right = cleanx(x, fromIso(periods[periods.length - 1].end)) + widthPerDay

  return {
	position: 'absolute',
	left: `${left}px`,
	width: `${right - left}px`,
	// Gemeint ist der Abstand nach AUSSEN, zu den Nachbarprojekten - ein Abzug
	// fuer den Innenabstand zum letzten Balken macht die Luft unten groesser
	// statt gleich.
	//
	// Oben bleiben 2 px mehr Luft als unten. Ausgeglichen wird das UNTEN durch
	// eine um 2 px geringere Hoehe, nicht oben durch ein negatives marginTop:
	// das zog die Oberkante in das darueberliegende Projekt hinein, so dass sich
	// bei zwei aufgeklappten Projekten die beiden Rahmen ueberlappten.
	//
	// subRows sind die Zeilen der Unterprojekte samt ihrer Kategorie-
	// Ueberschriften: der Rahmen klammert das Projekt mitsamt allem, was zu ihm
	// gehoert (Entwurf Bjoern, 04.09.2026).
	height: `${getRowHeight(props.isprintView) * (activeUsers.value.length + 1 + props.subRows) - 2}px`,
	// Abgedunkelt, weil eine 1-px-Linie in der rohen Pastellfarbe auf hellem
	// Grund kaum zu sehen ist - aber nur auf 3:1 statt der 4,5:1 der Zahlen.
	// WCAG verlangt fuer grafische Elemente ohnehin nur 3:1, und bei 4,5:1 wirkte
	// die Umrandung in der Abnahme zu kraeftig (rund 27 Prozent dunkler).
	border: `1px solid ${readableProjectColor(props.project.color, 3)}`,
	boxSizing: 'border-box',
	pointerEvents: 'none',
	zIndex: 1,
  }
}

// Der Rahmen eines Bereichs. Bewusst nicht dieselbe Linie wie beim
// Hauptprojekt: schwaecher in der Farbe, und links wie rechts um 4 px
// eingerueckt. Sonst laege er bei gleichem Zeitraum genau auf dem aeusseren
// Rahmen - zwei Linien uebereinander, die zusammen nur dicker aussehen.
function subprojectOutlineStyle() {
  const periods = displayPeriods.value
  const left  = cleanx(x, fromIso(periods[0].start))
  const right = cleanx(x, fromIso(periods[periods.length - 1].end)) + widthPerDay
  // Dieselbe Zeilenrechnung wie beim Grund: Titelzeile plus Personenzeilen,
  // solange aufgeklappt. Beide muessen uebereinstimmen, sonst sitzt der Rahmen
  // nicht auf der Kante seiner eigenen Flaeche.
  const zeilen = 1 + (props.isopen ? activeUsers.value.length : 0)

  const luft = bereichsEinzug()

  return {
	position: 'absolute',
	// Dieselbe Luft wie beim Grund, damit der Rahmen genau auf dessen Kante
	// sitzt und das Kaestchen sich vom Projektrahmen abhebt.
	marginTop: `${luft.y}px`,
	left: `${left + luft.x}px`,
	width: `${right - left - 2 * luft.x}px`,
	height: `${getRowHeight(props.isprintView) * zeilen - 2 * luft.y}px`,
	// Dieselbe Farbe wie der Projektrahmen, nicht abgeschwaecht.
	border: `1px solid ${readableProjectColor(props.project.color, 3)}`,
	borderRadius: '5px',
	boxSizing: 'border-box',
	pointerEvents: 'none',
	zIndex: 1,
  }
}

// Luft um das Kaestchen eines Bereichs (Entwurf Bjoern). Ohne sie liegt sein
// Rahmen genau auf dem des Hauptprojekts, solange beide denselben Zeitraum
// haben - man saehe dann gar kein eigenes Kaestchen. Grund und Rahmen ruecken
// gemeinsam ein, sonst schaute die Flaeche unter dem Rahmen hervor.
const LUFT_X = 5
const LUFT_Y = 2

function bereichsEinzug() {
  return props.project.parent_id ? { x: LUFT_X, y: LUFT_Y } : { x: 0, y: 0 }
}

function periodBackgroundStyle(period) {
  // Kopfzeile plus eigene Personenzeilen, solange aufgeklappt. Bewusst OHNE
  // subRows: die Bereiche malen ihren Grund selbst, jeder ueber seinen eigenen
  // Zeitraum. Nur so hoert die Flaeche dort auf, wo der Bereich aufhoert.
  const zeilen = 1 + (props.isopen ? activeUsers.value.length : 0);
  const luft = bereichsEinzug();
  const backgroundHeight = getRowHeight(props.isprintView) * zeilen - 2 * luft.y;

  return {
	position: 'absolute',
	marginTop: `${luft.y}px`,
	left: `${cleanx(x, fromIso(period.start)) + luft.x}px`,
	width: `${cleanx(x, fromIso(period.end)) - cleanx(x, fromIso(period.start)) + widthPerDay - 2 * luft.x}px`,
	height: `${backgroundHeight}px`,
	borderRadius: props.project.parent_id ? '5px' : undefined,
	backgroundColor: props.project.color + "33",
	zIndex: 0,
	// Reine Flaeche, keine Bedienung: seit der Hintergrund die Unterprojekte
	// mitumfasst, liegt er ueber deren Zeilen und verschluckte dort jeden Klick -
	// die Balken waren nicht mehr greifbar, nicht einmal der Mauszeiger aenderte
	// sich. Gleiches gilt fuer die Pausenschraffur und den Rahmen.
	pointerEvents: 'none',
  }
}

// cstart/cend kommen als ISO aus Task (Drag/Resize) – Modell-Typ, keine Umwandlung.
function projectChangeHanlder(cstart, cend) {
  startRef.value = cstart;
  endRef.value   = cend;
}

function handleBookingDone(params) {
  if (params["isproject"]) {
	  console.log("projectChangeDone");
	  emit('projectChangeDone', params)		
  } else {
    if (params["newpstart"]) {
      startRef.value = params["newpstart"]
      endRef.value = params["newpend"]
      console.log("startRef.value=" + startRef.value + " endRef.value=" + endRef.value)
      updateProject.value++;
    }
	  emit('bookingDone', params)		
  }
}

function handleCancel() {
  startRef.value = props.project.start;
  endRef.value   = props.project.end;
  emit('cancel');
}

function deselect(e) {
  emit('deselect')		
}


const pid2pnames = ref(props.projects.reduce((acc, entity) => {
  acc[entity.id] = entity.name
  return acc
}, {}));

function onProjectUpdated(data) {
  console.log('received', data)

  if (data["booking_action"] === props.project.id && data["newpstart"]) {
    startRef.value = data["newpstart"]
    endRef.value = data["newpend"]
    console.log("onProjectUpdated startRef.value=" + startRef.value + " endRef.value=" + endRef.value)
  }
	updateProject.value++;

}

onMounted(() => {
  bus.on('project-updated', onProjectUpdated)
})

onUnmounted(() => {
  bus.off('project-updated', onProjectUpdated)
})

</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  background: #f4f4f4;
  padding: 4px 8px;
  cursor: pointer;
}

.content {
  padding: 8px;
  background: #fff;
}

.toggle {
  font-size: 0.8em;
}

.resource-row {
  width: 100%;
  position: relative;
  box-sizing: border-box;
  overflow: hidden; /* or auto, depending on layout */
}

.resource-row.spacer {
  background-color: rgb(250, 250, 250);
}

</style>
